// PersonCard.tsx
import React, { useState, useCallback } from 'react';
import { Card, Avatar, Text, Modal, ActionIcon, Group, Menu, Box } from '@mantine/core';
import { IconDotsVertical, IconUserMinus, IconUsersGroup } from '@tabler/icons-react';
import { PersonDetailsModal } from './PersonDetailsModal';
import { RemovePersonModal } from './RemovePersonModal';
import { PersonBetter} from '../../types/Person';
import { MergePersonModalNew } from './MergePersonModalNew';

interface FamilyMemberProps {
    person: PersonBetter;
    onUpdate: (updatedMember: PersonBetter) => void;
    onRemove?: (person: PersonBetter) => void;
    onMerge?: (sourcePerson: PersonBetter, targetPerson: PersonBetter, mergeOptions: any) => void;
    familyMembers?: PersonBetter[];
}

export const PersonCard: React.FC<FamilyMemberProps> = React.memo(({ 
    person, 
    onUpdate, 
    onRemove, 
    onMerge, 
    familyMembers = []
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
    const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
    const [menuOpened, setMenuOpened] = useState(false);

    const handleCardClick = useCallback(() => {
        setIsModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    const handleUpdateMember = useCallback((updatedMember: PersonBetter) => {
        onUpdate(updatedMember);
    }, [onUpdate]);
    
    const handleRemovePerson = useCallback((personToRemove: PersonBetter) => {
        if (onRemove) {
            onRemove(personToRemove);
        } else {
            console.log('Remove person:', personToRemove);
        }
    }, [onRemove]);
    
    const handleMergePerson = useCallback((sourcePerson: PersonBetter, targetPerson: PersonBetter, mergeOptions: any) => {
        if (onMerge) {
            onMerge(sourcePerson, targetPerson, mergeOptions);
        } else {
            console.log('Merge request:', {
                sourcePerson,
                targetPerson,
                mergeOptions
            });
        }
    }, [onMerge]);

    return (
        <>
            <Card
                key={person.seed_key}
                shadow="xs"
                radius="md"
                style={{
                    width: 'calc((100% - 30px) / 4)',
                    aspectRatio: '1.0',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <Box
                    style={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 10,
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <Menu
                        opened={menuOpened}
                        onChange={setMenuOpened}
                        position="bottom-end"
                        // Use portal but with specific settings to prevent overflow issues
                        withinPortal={true}
                        portalProps={{ target: document.body }}
                        closeOnItemClick={true}
                        closeOnEscape={true}
                        // Use a different approach for focus management
                        trapFocus={false}
                        withArrow
                        styles={{ dropdown: { zIndex: 1000 } }}
                    >
                        <Menu.Target>
                            <ActionIcon variant="subtle" color="gray">
                                <IconDotsVertical size={18} />
                            </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                            <Menu.Item 
                                leftSection={<IconUsersGroup size={16} />} 
                                onClick={() => setIsMergeModalOpen(true)}
                            >
                                Merge with another person
                            </Menu.Item>
                            <Menu.Item 
                                leftSection={<IconUserMinus size={16} />} 
                                color="red"
                                onClick={() => setIsRemoveModalOpen(true)}
                            >
                                Remove person
                            </Menu.Item>
                        </Menu.Dropdown>
                    </Menu>
                </Box>
                
                <div 
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        cursor: 'pointer',
                    }}
                    onClick={handleCardClick}
                >
                    <Avatar
                        src={person.profile_url}
                        radius="xl"
                        alt={`${person.first} ${person.last} profile photo`}
                        style={{
                            width: '120px',
                            height: '120px',
                            marginBottom: '20px',
                        }}
                    />
                    <div style={{textAlign: 'center'}}>
                        <Text size="lg">{person.first} {person.last}</Text>
                        {/*todo: no time zones on dob! - fix this then uncomment*/}
                        {/*<Text size="sm" color="gray">*/}
                        {/*    {person.dob? person.dob.toString() : ""}*/}
                        {/*</Text>*/}
                    </div>
                </div>
            </Card>

            {isModalOpen && (
                <Modal
                    opened={isModalOpen}
                    onClose={handleCloseModal}
                    size="xl"
                    title={`${person.first} ${person.last} - Family Information`}
                >
                    <PersonDetailsModal
                        person={person}
                        onUpdate={handleUpdateMember}
                        onClose={handleCloseModal}
                        // familyMembersBasic={familyMembersBasic}
                    />
                </Modal>
            )}
            
            <RemovePersonModal
                opened={isRemoveModalOpen}
                onClose={() => setIsRemoveModalOpen(false)}
                person={person}
                onRemove={handleRemovePerson}
            />
            
            <MergePersonModalNew
                opened={isMergeModalOpen}
                onClose={() => setIsMergeModalOpen(false)}
                sourcePerson={person}
                targetPersons={familyMembers.filter(p => p.seed_key !== person.seed_key)}
                onMerge={handleMergePerson}
            />
        </>
    );
});